import React, { useState, useEffect, useRef } from "react";
import "../styles/Order.css";
import PendingIcon from "../icons/PendingIcon";
import ShippingBusIcon from "../icons/ShippingBusIcon";
import OrderBoxIcon from "../icons/OrderBoxIcon";
import ConfirmedIcon from "../icons/ConfirmedIcon";
import RejectXIcon from "../icons/RejectXIcon";
import CubeIcon from "../icons/CubeIcon";
import VendorOrderController from "../controllers/VendorOrderController";
import { userService } from "../services/userService";
import DateRangePicker from "./DateRangePicker";
import CustomDropdown from "./StatusDropdown";
import ArrowDownIcon from "../icons/BusinessHub/ArrowDownIcon";

const getFirstValue = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const formatTextStatus = (value) =>
  String(value || "")
    .toLowerCase()
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const formatApiCurrency = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const getInitials = (name, fallback) => {
  const parts = String(name || fallback || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  return (
    parts
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "OR"
  );
};

const normalizeOrderStatus = (orderStatus) => {
  const status = String(orderStatus || "").toUpperCase();

  if (
    ["PENDING", "EMPTY", "DRAFT", "PENDING_PAYMENT", "VENDOR_PENDING"].includes(
      status,
    )
  ) {
    return "pending";
  }

  if (
    [
      "ACTIVE",
      "PAYMENT_CONFIRMED",
      "VENDOR_ACCEPTED",
      "PREPARATION_IN_PROGRESS",
      "READY_FOR_SHIPMENT",
      "PICKED_UP_BY_CARRIER",
    ].includes(status)
  ) {
    return "confirmed";
  }

  if (["IN_TRANSIT", "OUT_FOR_DELIVERY"].includes(status)) {
    return "out_for_delivery";
  }

  if (["DELIVERED", "DELIVERY_CONFIRMED"].includes(status)) {
    return "delivered";
  }

  if (["VENDOR_REJECTED", "CANCELLED", "PAYMENT_FAILED"].includes(status)) {
    return "rejected";
  }

  return "pending";
};

const normalizeOrderProgress = (orderStatus) => {
  const status = String(orderStatus || "").toUpperCase();

  if (["VENDOR_REJECTED", "CANCELLED", "PAYMENT_FAILED"].includes(status)) {
    return "rejected";
  }

  if (
    [
      "VENDOR_ACCEPTED",
      "PREPARATION_IN_PROGRESS",
      "READY_FOR_SHIPMENT",
      "PICKED_UP_BY_CARRIER",
      "IN_TRANSIT",
      "OUT_FOR_DELIVERY",
      "DELIVERED",
      "DELIVERY_CONFIRMED",
    ].includes(status)
  ) {
    return status.toLowerCase();
  }

  return "vendor_pending";
};

const getOrderTimestamp = (order) => {
  const timestamp = getFirstValue(
    order?.createdAt,
    order?.createdDate,
    order?.orderDate,
    order?.updatedAt,
    order?.paymentDate,
  );

  if (!timestamp) {
    return order?.paymentStatus
      ? `Payment: ${formatTextStatus(order.paymentStatus)}`
      : "";
  }

  const date = new Date(timestamp);

  if (Number.isNaN(date.getTime())) {
    return String(timestamp);
  }

  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getOrderAddress = (order) =>
  getFirstValue(
    order?.deliveryAddress,
    order?.shippingAddress,
    order?.address,
    order?.customerAddress,
    order?.location,
    order?.deliveryLocation,
    order?.delivery?.address,
    order?.customer?.address,
    "Delivery address not provided",
  );

const normalizeOrderItem = (item = {}) => {
  const unitPrice = Number(getFirstValue(item.unitPrice, item.price, 0));
  const quantity = Number(getFirstValue(item.quantity, 1));
  const lineTotal = Number(getFirstValue(item.lineTotal, unitPrice * quantity));

  return {
    ...item,
    product: getFirstValue(
      item.displayName,
      item.productName,
      item.name,
      "Product",
    ),
    sku: getFirstValue(item.skuId, item.sku, item.productId, "N/A"),
    quantity,
    unitPrice,
    lineTotal,
    itemPrice: formatApiCurrency(lineTotal),
    itemPriceEach: `${formatApiCurrency(unitPrice)} each`,
  };
};

const getOrdersFromResponse = (response) => {
  const data = response?.data ?? response;

  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.orders)) return data.orders;
  if (Array.isArray(data?.data)) return data.data;

  return [];
};

const toApiDate = (value) => {
  if (!value) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  return match ? `${match[3]}-${match[2]}-${match[1]}` : value;
};

const getDashboardView = (tabKey) => {
  if (tabKey === "A") return "ORDER_HISTORY";
  if (tabKey === "B") return "PENDING";
  if (tabKey === "C") return "CONFIRMED";
  if (tabKey === "D") return "OUT_FOR_DELIVERY";
  return "PENDING";
};

const getStatusLabel = (value) =>
  String(value || "")
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(" ");

const normalizeOrder = (order = {}) => {
  const items = Array.isArray(order.items)
    ? order.items.map(normalizeOrderItem)
    : [];
  const fallbackFirstItem =
    items[0] ||
    normalizeOrderItem({
      productName: order.primaryProductName,
      skuId: order.primarySkuCode,
      quantity: getFirstValue(order.primaryQuantity, 1),
      unitPrice: getFirstValue(order.primaryUnitPrice, order.amount, 0),
      lineTotal:
        Number(getFirstValue(order.primaryQuantity, 1)) *
        Number(getFirstValue(order.primaryUnitPrice, order.amount, 0)),
    });
  const firstItem = fallbackFirstItem;
  const resolvedStatus = getFirstValue(order.orderStatus, order.status);
  const id = getFirstValue(order.orderNumber, order.orderId, order.id, "ORDER");
  const customer = getFirstValue(
    order.customerName,
    order.customerFullName,
    order.userName,
    order.customer?.fullName,
    order.customer?.name,
    "Customer",
  );
  const itemsTotal = items.reduce((total, item) => total + item.lineTotal, 0);
  const subtotalAmount = Number(
    getFirstValue(order.totalAmount, order.subtotalAmount, itemsTotal),
  );

  return {
    ...order,
    id: String(id).startsWith("#") ? String(id) : `#${id}`,
    rawOrderId: order.orderId,
    customer,
    time: getOrderTimestamp(order),
    product:
      items.length > 1
        ? `${firstItem.product} + ${items.length - 1} more`
        : firstItem.product,
    sku: firstItem.sku,
    quantity:
      items.reduce((total, item) => total + item.quantity, 0) ||
      Number(getFirstValue(order.primaryQuantity, 1)),
    itemPrice: formatApiCurrency(subtotalAmount),
    itemPriceEach: firstItem.itemPriceEach,
    address: getOrderAddress(order),
    initials: getInitials(customer, id),
    totalAmount: Number(
      getFirstValue(order.totalAmount, order.subtotalAmount, itemsTotal),
    ),
    taxAmount: Number(getFirstValue(order.taxAmount, 0)),
    taxPercentage: Number(getFirstValue(order.taxPercentage, 0)),
    status: normalizeOrderStatus(resolvedStatus),
    progress: normalizeOrderProgress(resolvedStatus),
    driver: getFirstValue(
      order.driverName,
      order.deliveryDriverName,
      order.driver?.fullName,
      order.driver?.name,
      "Unassigned",
    ),
    items: items.length ? items : [firstItem],
    subtotalAmount,
  };
};

function Order() {
  const TAX_RATE = 0.0833;
  const statusLabels = {
    pending: "Pending",
    confirmed: "Confirmed",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    rejected: "Rejected",
  };
  const progressSteps = [
    {
      key: "vendor_pending",
      title: "Vendor Pending",
      text: "Completed or in progress",
    },
    {
      key: "vendor_accepted",
      title: "Vendor Accepted",
      text: "Completed or in progress",
    },
    {
      key: "preparation_in_progress",
      title: "Preparation In Progress",
      text: "Waiting",
    },
    {
      key: "ready_for_shipment",
      title: "Ready For Shipment",
      text: "Waiting",
    },
    {
      key: "picked_up_by_carrier",
      title: "Picked Up By Carrier",
      text: "Waiting",
    },
    {
      key: "in_transit",
      title: "In Transit",
      text: "Waiting",
    },
    {
      key: "out_for_delivery",
      title: "Out For Delivery",
      text: "Waiting",
    },
    {
      key: "delivered",
      title: "Delivered",
      text: "Waiting",
    },
    {
      key: "delivery_confirmed",
      title: "Delivery Confirmed",
      text: "Waiting",
    },
  ];
  const tabs = [
    {
      key: "A",
      title: "Order History",
      text: "All orders on platform",
      icon: "processed",
      statuses: null,
    },
    {
      key: "B",
      title: "Pending",
      text: "Awaiting your response",
      icon: "revenue",
      statuses: ["pending"],
    },
    {
      key: "C",
      title: "Confirmed",
      text: "Accepted and delivery progress",
      icon: "payouts",
      statuses: ["confirmed", "delivered"],
    },
    {
      key: "D",
      title: "Out for Delivery",
      text: "En route to customers",
      icon: "percentage",
      statuses: ["out_for_delivery"],
    },
  ];

  const [active, setActive] = useState("A");
  const [orders, setOrders] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalOrders: 0,
    pendingCount: 0,
    confirmedCount: 0,
    outForDeliveryCount: 0,
  });
  const [dateRange, setDateRange] = useState({
    fromDate: "",
    toDate: "",
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 0,
    size: 8,
    totalPages: 0,
  });
  const [isLoadingOrders, setIsLoadingOrders] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const viewerRef = useRef(null);
  const viewerTriggerRef = useRef(null);
  const openViewer = () => {
    if (!isViewerOpen) viewerTriggerRef.current = document.activeElement;
    setIsViewerOpen(true);
  };

  useEffect(() => {
    if (!isViewerOpen) return;

    const dialog = viewerRef.current;
    const trigger = viewerTriggerRef.current;
    const previousOverflow = document.body.style.overflow;
    dialog.showModal();
    document.body.style.overflow = "hidden";

    return () => {
      dialog.close();
      document.body.style.overflow = previousOverflow;
      if (trigger instanceof HTMLElement && trigger.isConnected) {
        trigger.focus({ preventScroll: true });
      }
    };
  }, [isViewerOpen]);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);
  const [selectedNextStatus, setSelectedNextStatus] = useState("");
  const [orderDetailsError, setOrderDetailsError] = useState("");
  const [orderActionError, setOrderActionError] = useState("");
  const [filterError, setFilterError] = useState("");
  const [isSubmittingAction, setIsSubmittingAction] = useState(false);
  const [pendingOrderAction, setPendingOrderAction] = useState("");
  const orderActionInFlightRef = useRef(false);
  const activeTab = tabs.find((tab) => tab.key === active);
  const visibleOrders = orders;

  const formatCurrency = (amount) => `$${amount.toFixed(2)}`;
  const getOrderPricing = (order) => {
    const subtotal = Number(
      getFirstValue(
        order.subtotalAmount,
        String(order.itemPrice || "").replace(/[^0-9.]/g, ""),
        0,
      ),
    );
    const providedTotal = Number(getFirstValue(order.totalAmount, subtotal));
    const providedTax = Number(
      getFirstValue(order.taxAmount, Math.max(0, providedTotal - subtotal)),
    );
    const tax =
      Number.isFinite(providedTax) && providedTax >= 0
        ? providedTax
        : subtotal * TAX_RATE;
    const total =
      Number.isFinite(providedTotal) && providedTotal >= 0
        ? providedTotal
        : subtotal + tax;

    return {
      subtotal: formatCurrency(subtotal),
      tax: formatCurrency(tax),
      total: formatCurrency(total),
    };
  };
  const getTabCount = (tab) => {
    if (tab.key === "A") return dashboardStats.totalOrders || 0;
    if (tab.key === "B") return dashboardStats.pendingCount || 0;
    if (tab.key === "C") return dashboardStats.confirmedCount || 0;
    if (tab.key === "D") return dashboardStats.outForDeliveryCount || 0;
    return 0;
  };
  const getProgressIndex = (order) =>
    progressSteps.findIndex((step) => step.key === order.progress);
  const getStepState = (order, index) => {
    const progressIndex = getProgressIndex(order);

    if (progressIndex === -1) {
      return "pending";
    }
    if (index < progressIndex) {
      return "complete";
    }
    if (index === progressIndex) {
      return order.status === "delivered" ? "complete" : "current";
    }
    return "pending";
  };
  const handleTabClick = (key) => {
    setActive(key);
    setSelectedOrder(null);
    setSelectedOrderDetails(null);
    setOrderDetailsError("");
    setOrderActionError("");
    setPagination((current) => ({ ...current, page: 0 }));
  };
  const handleOrderClick = (order) => {
    openViewer();
    setSelectedOrder(order);
    setSelectedOrderDetails(null);
    setOrderDetailsError("");
    setOrderActionError("");
    loadDashboard({
      nextSelectedOrder: order,
    });
  };
  const loadDashboard = async ({
    nextTab = active,
    nextPage = pagination.page,
    nextSelectedOrder = selectedOrder,
    nextDateRange = dateRange,
  } = {}) => {
    setIsLoadingOrders(true);
    setOrderDetailsError("");

    try {
      const response = await VendorOrderController.getOrdersDashboard({
        Authorization: userService.getUser(),
        companyId: userService.getCompID(),
        view: getDashboardView(nextTab),
        selectedOrderId: nextSelectedOrder?.rawOrderId,
        fromDate: toApiDate(nextDateRange.fromDate),
        toDate: toApiDate(nextDateRange.toDate),
        page: nextPage,
        size: pagination.size,
      });
      const data = response?.data ?? response ?? {};
      const normalizedOrders = getOrdersFromResponse(data).map(normalizeOrder);
      const selectedDetails = data?.selectedOrder ?? null;
      const selectedId = String(
        getFirstValue(
          selectedDetails?.orderId,
          nextSelectedOrder?.rawOrderId,
          normalizedOrders[0]?.rawOrderId,
          "",
        ),
      );
      const nextSelected =
        normalizedOrders.find(
          (order) => String(order.rawOrderId || "") === selectedId,
        ) ||
        normalizedOrders[0] ||
        null;

      setOrders(normalizedOrders);
      setDashboardStats({
        totalOrders: Number(data?.stats?.orderHistory || 0),
        pendingCount: Number(data?.stats?.pending || 0),
        confirmedCount: Number(data?.stats?.confirmed || 0),
        outForDeliveryCount: Number(data?.stats?.outForDelivery || 0),
      });
      setPagination({
        total: Number(data?.total || 0),
        page: Number(data?.page || 0),
        size: Number(data?.size || pagination.size),
        totalPages: Number(data?.totalPages || 0),
      });
      setSelectedOrder(nextSelected);
      setSelectedOrderDetails(selectedDetails);
      setSelectedNextStatus(
        Array.isArray(selectedDetails?.availableNextStatuses) &&
          selectedDetails.availableNextStatuses.length > 0
          ? selectedDetails.availableNextStatuses[0]
          : "",
      );
    } catch (error) {
      console.error("Failed to load orders dashboard", error);
      setOrders([]);
      setSelectedOrder(null);
      setSelectedOrderDetails(null);
      setSelectedNextStatus("");
      setDashboardStats({
        totalOrders: 0,
        pendingCount: 0,
        confirmedCount: 0,
        outForDeliveryCount: 0,
      });
      setPagination((current) => ({
        ...current,
        total: 0,
        totalPages: 0,
      }));
      setOrderDetailsError("Failed to load orders.");
    } finally {
      setIsLoadingOrders(false);
    }
  };
  const handleOrderAction = async (action) => {
    const targetOrder = selectedOrderDetails || selectedOrder;
    const orderId = getFirstValue(
      targetOrder?.rawOrderId,
      targetOrder?.orderId,
      selectedOrder?.rawOrderId,
    );
    const isPending = selectedOrderDetails
      ? String(selectedOrderDetails.status || "").toUpperCase() === "VENDOR_PENDING"
      : selectedOrder?.status === "pending";

    if (!orderId || !isPending || isLoadingOrders || isSubmittingAction || orderActionInFlightRef.current) {
      return;
    }

    orderActionInFlightRef.current = true;
    setIsSubmittingAction(true);
    setPendingOrderAction(action);
    setOrderActionError("");

    try {
      const submit = action === "accept"
        ? VendorOrderController.acceptOrder
        : VendorOrderController.rejectOrder;
      await submit({ Authorization: userService.getUser(), orderId });
      await loadDashboard({ nextSelectedOrder: null });
      setIsViewerOpen(false);
    } catch (error) {
      console.error(`Failed to ${action} order`, error);
      setOrderActionError(
        `Failed to ${action === "accept" ? "accept" : "reject"} order. Please try again.`,
      );
    } finally {
      orderActionInFlightRef.current = false;
      setPendingOrderAction("");
      setIsSubmittingAction(false);
    }
  };
  const handleStatusUpdate = async () => {
    if (
      !selectedOrder?.rawOrderId ||
      !selectedNextStatus ||
      isSubmittingAction
    ) {
      return;
    }

    setIsSubmittingAction(true);
    setOrderActionError("");

    try {
      await VendorOrderController.updateStatus({
        Authorization: userService.getUser(),
        request: {
          orderId: selectedOrder.rawOrderId,
          status: selectedNextStatus,
        },
      });

      await loadDashboard({
        nextSelectedOrder: selectedOrder,
      });
    } catch (error) {
      console.error("Failed to update order status", error);
      setOrderActionError("Failed to update order status.");
    } finally {
      setIsSubmittingAction(false);
    }
  };
  const handleDateRangeChange = ({ from, to }) => {
    setDateRange({
      fromDate: from,
      toDate: to,
    });
    setPagination((current) => ({ ...current, page: 0 }));
  };
  const handleDateApply = () => {
    const apiFromDate = toApiDate(dateRange.fromDate);
    const apiToDate = toApiDate(dateRange.toDate);

    if (apiFromDate && apiToDate && apiFromDate > apiToDate) {
      setFilterError("From date cannot be after the to date.");
      return;
    }

    setFilterError("");
    loadDashboard({
      nextPage: 0,
      nextSelectedOrder: null,
    });
  };
  const handleDateReset = () => {
    const clearedRange = {
      fromDate: "",
      toDate: "",
    };

    setFilterError("");
    setDateRange(clearedRange);
    setPagination((current) => ({ ...current, page: 0 }));
    loadDashboard({
      nextPage: 0,
      nextSelectedOrder: null,
      nextDateRange: clearedRange,
    });
  };
  const handlePageChange = (direction) => {
    const nextPage = pagination.page + direction;

    if (nextPage < 0 || nextPage >= pagination.totalPages) {
      return;
    }

    setSelectedOrder(null);
    setSelectedOrderDetails(null);
    setPagination((current) => ({ ...current, page: nextPage }));
  };
  const rowClass = (key) =>
    active === key ? "order-body-card-selected" : "order-body-card";
  const renderTabIcon = (icon) => {
    if (icon === "payouts") {
      return <ConfirmedIcon />;
    }
    if (icon === "percentage") {
      return <ShippingBusIcon />;
    }

    return <PendingIcon />;
  };
  const renderOrderCard = (order) => (
    <div
      key={order.id}
      className={`order-history-cards ${
        selectedOrder?.id === order.id ? "active" : ""
      }`}
      tabIndex={0}
      onClick={(event) => {
        event.currentTarget.focus({ preventScroll: true });
        handleOrderClick(order);
      }}
      onKeyDown={(event) => {
        if (
          event.target === event.currentTarget &&
          ["Enter", " "].includes(event.key)
        ) {
          event.preventDefault();
          handleOrderClick(order);
        }
      }}
    >
      <div className="order-history-cards-body">
        <div className="order-history-card-row">
          <div className="order-history-card-body-header">
            <div className="order-history-card-header">{order.initials}</div>
            <div className="order-history-card-text">
              <div className="order-history-card-text-title">
                {order.id}
                <span className={`order-card-state card-state-${order.status}`}>
                  {statusLabels[order.status]}
                </span>
              </div>
              <div className="order-history-card-text-body">
                {order.customer} - {order.time}
              </div>
              <div className="order-history-card-text-body">
                {order.customerPhoneNumber}
              </div>
            </div>
          </div>
          <div className="order-history-card-footer">
            {getOrderPricing(order).total}
          </div>
        </div>

        <div className="order-history-card-row">
          <div className="order-history-card-body-header">
            <div className="order-history-card-text">
              <div className="order-history-card-text-title">
                {order.product}
              </div>
              <div className="order-history-card-text-body">
                SKU: {order.sku} Qty: {order.quantity}
              </div>
            </div>
          </div>
          <div className="order-history-card-footer">
            <div className="order-history-card-footer-title">
              {order.itemPrice}
            </div>
            <div className="order-history-card-footer-text">
              {order.itemPriceEach}
            </div>
          </div>
        </div>

        <div className="order-history-card-row">
          <div className="order-history-card-body-header">
            <div className="order-history-card-text">
              <div className="order-history-card-text-body">
                {order.address}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
  const renderOrderRow = (order) => (
    <tr
      key={order.id}
      className={selectedOrder?.id === order.id ? "active" : ""}
      tabIndex={-1}
      onClick={(event) => {
        event.currentTarget.focus({ preventScroll: true });
        handleOrderClick(order);
      }}
    >
      <td>
        <button
          type="button"
          className="orders-table-order-link"
          aria-label={`View order ${order.id}`}
          aria-pressed={selectedOrder?.id === order.id}
          onClick={(event) => {
            event.stopPropagation();
            handleOrderClick(order);
          }}
        >
          {order.id}
        </button>
        <span className={`order-card-state card-state-${order.status}`}>
          {statusLabels[order.status]}
        </span>
      </td>
      <td>
        <div className="orders-table-customer">
          <span className="orders-table-avatar">{order.initials}</span>
          <div>
            <div className="orders-table-primary">{order.customer}</div>
            <div className="orders-table-secondary">
              {order.customerPhoneNumber}
            </div>
            <div className="orders-table-secondary">{order.time}</div>
          </div>
        </div>
      </td>
      {/* <td>
        <div className="orders-table-primary">{order.product}</div>
        <div className="orders-table-secondary">SKU: {order.sku}</div>
        <div className="orders-table-secondary">Qty: {order.quantity}</div>
      </td> */}
      <td className="orders-table-price">
        <div className="orders-table-primary">{order.itemPrice}</div>
        <div className="orders-table-secondary">{order.itemPriceEach}</div>
      </td>
      <td className="orders-table-price">
        <div className="orders-table-total">{getOrderPricing(order).total}</div>
      </td>
      <td className="orders-table-address">{order.address}</td>
    </tr>
  );
  const renderProgress = (order) => (
    <div className="order-history-viewer-progress">
      <div className="order-history-viewer-section-title">
        Delivery Progress
      </div>
      <div className="order-history-driver-card">
        <div className="order-history-driver-avatar">
          {order.driver
            ?.split(" ")
            .map((part) => part[0])
            .join("")}
        </div>
        <div className="order-history-driver-info">
          <div className="order-history-driver-name">{order.driver}</div>
          <div className="order-history-viewer-muted">Assigned driver</div>
        </div>
        <div className="order-history-driver-state">Active</div>
      </div>
      <div className="order-history-timeline">
        {progressSteps.map((step, index) => {
          const stepState = getStepState(order, index);

          return (
            <div
              key={step.key}
              className={`order-history-timeline-step ${stepState}`}
            >
              <div className="order-history-timeline-marker">
                {stepState === "complete" && <span />}
              </div>
              <div className="order-history-timeline-content">
                <div className="order-history-timeline-title">{step.title}</div>
                <div className="order-history-timeline-text">{step.text}</div>
              </div>
            </div>
          );
        })}
      </div>
      {order.status === "delivered" && (
        <div className="order-history-delivered-banner">
          <ConfirmedIcon stroke="#ffffff" w="17px" h="17px" />
          Delivered Successfully.
        </div>
      )}
    </div>
  );
  const renderDetailsProgress = (orderDetails) => {
    const assignedDriver = orderDetails.assignedDriver || {};
    const deliveryProgress = Array.isArray(orderDetails.deliveryProgress)
      ? orderDetails.deliveryProgress
      : [];
    const showDriverCard = Boolean(assignedDriver.name);

    return (
      <div className="order-history-viewer-progress">
        <div className="order-history-viewer-section-title">
          Delivery Progress
        </div>
        {showDriverCard && (
          <div className="order-history-driver-card">
            <div className="order-history-driver-avatar">
              {assignedDriver.initials ||
                getInitials(assignedDriver.name, "DR")}
            </div>
            <div className="order-history-driver-info">
              <div className="order-history-driver-name">
                {assignedDriver.name || "Unassigned"}
              </div>
              <div className="order-history-viewer-muted">Assigned driver</div>
            </div>
            <div className="order-history-driver-state">
              {assignedDriver.status || "Active"}
            </div>
          </div>
        )}
        <div className="order-history-timeline">
          {deliveryProgress.map((step) => {
            const stepState = step.completed
              ? "complete"
              : step.active
                ? "current"
                : "pending";

            return (
              <div
                key={step.key || step.title}
                className={`order-history-timeline-step ${stepState}`}
              >
                <div className="order-history-timeline-marker">
                  {step.completed && <span />}
                </div>
                <div className="order-history-timeline-content">
                  <div className="order-history-timeline-title">
                    {step.title}
                  </div>
                  <div className="order-history-timeline-text">
                    {step.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };
  const renderPendingActions = () => (
    <div className="orders-table-actions" aria-busy={isSubmittingAction}>
      <button
        type="button"
        disabled={isSubmittingAction || isLoadingOrders}
        className="orders-table-accept"
        onClick={() => handleOrderAction("accept")}
      >
        <ConfirmedIcon stroke="#ffffff" w="18px" h="18px" />
        {pendingOrderAction === "accept" ? "Accepting..." : "Accept Order"}
      </button>
      <button
        type="button"
        disabled={isSubmittingAction || isLoadingOrders}
        className="orders-table-reject"
        onClick={() => handleOrderAction("reject")}
      >
        <RejectXIcon />
        {pendingOrderAction === "reject" ? "Rejecting..." : "Reject"}
      </button>
    </div>
  );
  const renderDetailsViewer = () => {
    if (isLoadingOrders && selectedOrderDetails === null) {
      return (
        <div className="orders-drawer-content">
          <div className="order-history-viewer-loading">
            Loading order details...
          </div>
        </div>
      );
    }

    if (orderDetailsError) {
      return (
        <div className="orders-drawer-content">
          <div className="order-history-viewer-loading">
            {orderDetailsError}
          </div>
        </div>
      );
    }

    if (!selectedOrderDetails) {
      return null;
    }

    const items = Array.isArray(selectedOrderDetails.items)
      ? selectedOrderDetails.items
      : [];
    const taxPercentage = Number(selectedOrderDetails.taxPercentage || 0);
    const hasProgress =
      Array.isArray(selectedOrderDetails.deliveryProgress) &&
      selectedOrderDetails.deliveryProgress.length > 0;
    const availableNextStatuses = Array.isArray(
      selectedOrderDetails.availableNextStatuses,
    )
      ? selectedOrderDetails.availableNextStatuses
      : [];
    const canAdvanceStatus =
      availableNextStatuses.length > 0 &&
      String(selectedOrderDetails.status || "").toUpperCase() !==
        "VENDOR_PENDING";
    const canRespondToPending =
      String(selectedOrderDetails.status || "").toUpperCase() ===
      "VENDOR_PENDING";

    return (
      <div className="orders-drawer-layout">
        <div className="orders-drawer-content">
          <div className="order-history-viewer-customer">
            <div className="order-history-viewer-avatar">
              {selectedOrderDetails.customerInitials ||
                getInitials(selectedOrderDetails.customerName, "CU")}
            </div>
            <div className="order-history-viewer-customer-text">
              <div className="order-history-viewer-customer-name">
                {selectedOrderDetails.customerName}
              </div>
              <div className="order-history-viewer-muted">
                {selectedOrderDetails.placedAtLabel}
              </div>
            </div>
          </div>

          <div className="order-history-viewer-body">
            <div className="order-history-viewer-section-title">Products</div>
            {items.map((item) => (
              <div
                key={`${item.cartId || selectedOrderDetails.orderId}-${item.skuId}`}
                className="order-history-viewer-product-row"
              >
                <div className="order-history-viewer-product-icon">
                  <CubeIcon />
                </div>
                <div className="order-history-viewer-product-info">
                  <div className="order-history-viewer-product-name">
                    {item.displayName || item.productName}
                  </div>
                  <div className="order-history-viewer-muted">
                    SKU: {item.skuId}
                  </div>
                  <div className="order-history-viewer-muted">
                    Qty: {item.quantity} - {formatApiCurrency(item.unitPrice)}{" "}
                    each
                  </div>
                </div>
                <div className="order-history-viewer-product-price">
                  {formatApiCurrency(item.lineTotal)}
                </div>
              </div>
            ))}
          </div>

          <div className="order-history-viewer-summary">
            <div className="order-history-viewer-summary-row">
              <span>Subtotal</span>
              <span>{formatApiCurrency(selectedOrderDetails.subtotal)}</span>
            </div>
            <div className="order-history-viewer-summary-row">
              <span>Tax({taxPercentage}%)</span>
              <span>{formatApiCurrency(selectedOrderDetails.taxAmount)}</span>
            </div>
            <div className="order-history-viewer-total-row">
              <span>Total</span>
              <span>{formatApiCurrency(selectedOrderDetails.totalAmount)}</span>
            </div>
          </div>

          <div className="order-history-viewer-location">
            <div className="order-history-viewer-section-title">
              Delivery Location
            </div>
            <div className="order-history-viewer-address">
              {selectedOrderDetails.deliveryLocation}
            </div>
          </div>

          {hasProgress && renderDetailsProgress(selectedOrderDetails)}
        </div>
        {(canAdvanceStatus ||
          canRespondToPending ||
          orderActionError) && (
          <footer
            className="orders-drawer-footer"
            role="group"
            aria-label="Order actions"
          >
            {orderActionError && (
              <div className="order-history-viewer-error" role="alert">
                {orderActionError}
              </div>
            )}

            {canAdvanceStatus && (
              <div className="order-history-status-update">
                <div className="order-history-viewer-section-title">
                  Update Order Status
                </div>
                <div className="order-history-status-update-row">
                  <CustomDropdown
                    className="order-history-status-select"
                    menuClassName="order-history-status-select-menu"
                    optionClassName="order-history-status-select-option"
                    value={selectedNextStatus}
                    onChange={setSelectedNextStatus}
                    options={availableNextStatuses.map((status) => ({
                      label: getStatusLabel(status),
                      value: status,
                    }))}
                    rightIcon={<ArrowDownIcon />}
                  />
                  <button
                    type="button"
                    className="order-history-filter-action primary"
                    onClick={handleStatusUpdate}
                    disabled={!selectedNextStatus || isSubmittingAction}
                  >
                    {isSubmittingAction ? "Saving..." : "Update"}
                  </button>
                </div>
              </div>
            )}
            {canRespondToPending && renderPendingActions()}
          </footer>
        )}
      </div>
    );
  };
  const renderViewer = () => {
    if (selectedOrder === null) {
      return (
        <div className="orders-drawer-content" role="status">
          {orderDetailsError ||
            "This order is no longer available in the current list."}
        </div>
      );
    }

    const detailsViewer = renderDetailsViewer();

    if (detailsViewer) {
      return detailsViewer;
    }

    const pricing = getOrderPricing(selectedOrder);
    const showProgress = [
      "confirmed",
      "out_for_delivery",
      "delivered",
    ].includes(selectedOrder.status);
    const canRespondToPending = selectedOrder.status === "pending";

    return (
      <div className="orders-drawer-layout">
        <div className="orders-drawer-content">
          <div className="order-history-viewer-customer">
            <div className="order-history-viewer-avatar">
              {selectedOrder.initials}
            </div>
            <div className="order-history-viewer-customer-text">
              <div className="order-history-viewer-customer-name">
                {selectedOrder.customer}
              </div>
              <div className="order-history-viewer-muted">
                {selectedOrder.time}
              </div>
            </div>
          </div>

          <div className="order-history-viewer-body">
            <div className="order-history-viewer-section-title">Products</div>
            {selectedOrder.items.map((item) => (
              <div
                key={`${selectedOrder.id}-${item.sku}-${item.product}`}
                className="order-history-viewer-product-row"
              >
                <div className="order-history-viewer-product-icon">
                  <CubeIcon />
                </div>
                <div className="order-history-viewer-product-info">
                  <div className="order-history-viewer-product-name">
                    {item.product}
                  </div>
                  <div className="order-history-viewer-muted">
                    SKU: {item.sku}
                  </div>
                  <div className="order-history-viewer-muted">
                    Qty: {item.quantity} - {item.itemPriceEach}
                  </div>
                </div>
                <div className="order-history-viewer-product-price">
                  {item.itemPrice}
                </div>
              </div>
            ))}
          </div>

          <div className="order-history-viewer-summary">
            <div className="order-history-viewer-summary-row">
              <span>Subtotal</span>
              <span>{pricing.subtotal}</span>
            </div>
            <div className="order-history-viewer-summary-row">
              <span>Tax(8.33%)</span>
              <span>{pricing.tax}</span>
            </div>
            <div className="order-history-viewer-total-row">
              <span>Total</span>
              <span>{pricing.total}</span>
            </div>
          </div>

          <div className="order-history-viewer-location">
            <div className="order-history-viewer-section-title">
              Delivery Location
            </div>
            <div className="order-history-viewer-address">
              {selectedOrder.address}
            </div>
          </div>

          {showProgress && renderProgress(selectedOrder)}
        </div>
        {((!showProgress && canRespondToPending) || orderActionError) && (
          <footer
            className="orders-drawer-footer"
            role="group"
            aria-label="Order actions"
          >
            {orderActionError && (
              <div className="order-history-viewer-error" role="alert">
                {orderActionError}
              </div>
            )}

            {!showProgress && canRespondToPending && renderPendingActions()}
          </footer>
        )}
      </div>
    );
  };
  // We only auto-refresh on tab/page changes; date changes apply manually.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadDashboard({
      nextTab: active,
      nextPage: pagination.page,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, pagination.page]);
  return (
    <div className="payment-main-container orders-page">
      <dialog
        ref={viewerRef}
        className="orders-details-dialog"
        aria-labelledby="orders-details-title"
        onCancel={(event) => {
          event.preventDefault();
          setIsViewerOpen(false);
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) setIsViewerOpen(false);
        }}
      >
        {isViewerOpen && (
          <div className="orders-drawer-shell">
            <header className="orders-drawer-header">
              <div className="order-history-viewer-header-content">
                <div className="order-history-viewer-label">Order details</div>
                <h2
                  id="orders-details-title"
                  className="order-history-viewer-order-id"
                >
                  {selectedOrderDetails?.orderNumber ||
                    selectedOrderDetails?.orderId ||
                    selectedOrder?.id ||
                    "Order"}
                </h2>
                {selectedOrder && (
                  <span
                    className={`order-history-viewer-state viewer-state-${selectedOrderDetails ? normalizeOrderStatus(selectedOrderDetails.status) : selectedOrder.status}`}
                  >
                    {selectedOrderDetails
                      ? formatTextStatus(selectedOrderDetails.status)
                      : statusLabels[selectedOrder.status]}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="orders-drawer-close"
                onClick={() => setIsViewerOpen(false)}
                autoFocus
              >
                <span className="orders-drawer-back">&larr; Back</span>
                <span className="orders-drawer-close-label">Close &times;</span>
              </button>
            </header>
            {renderViewer()}
          </div>
        )}
      </dialog>
      <div className="payment-header">
        <div className="payment-header-wrapper">
          <div className="payment-header-icon">
            <OrderBoxIcon strockWidth="2.5px" height="24px" />
          </div>

          <div className="payment-title-container">
            <div className="payment-title">Orders</div>
            <div className="payment-header-text">
              manage incoming requests and track active deliveries
            </div>
          </div>
        </div>
      </div>
      <div className="payment-body-comtainer">
        {tabs.map((tab) => (
          <div
            key={tab.key}
            className={rowClass(tab.key)}
            onClick={() => handleTabClick(tab.key)}
          >
            <div className="order-body-card-header">
              <div className={`order-body-card-${tab.icon}-icon`}>
                {renderTabIcon(tab.icon)}
              </div>
              <div className="order-body-card-status">{getTabCount(tab)}</div>
            </div>
            <div className="order-body-card-title">{tab.title}</div>
            <div className="order-body-card-content">{tab.text}</div>
          </div>
        ))}
      </div>
      <div className="payment-analysis-comtainer">
        <div className="order-history-toolbar">
          <div className="order-history-toolbar-title">
            {activeTab?.title || "Orders"}
          </div>
          <div className="order-history-toolbar-controls">
            <DateRangePicker
              compact
              buttonLike
              buttonLabel="Date Range"
              className="order-history-date-range"
              fromValue={dateRange.fromDate}
              toValue={dateRange.toDate}
              onRangeChange={handleDateRangeChange}
            />
            <button
              type="button"
              className="order-history-filter-action primary"
              onClick={handleDateApply}
            >
              Apply
            </button>
            <button
              type="button"
              className="order-history-filter-action"
              onClick={handleDateReset}
            >
              Reset
            </button>
          </div>
        </div>
        {filterError && (
          <div className="order-history-toolbar-error">{filterError}</div>
        )}
        <div className="order-history-body">
          <div className="order-history-body-container">
            <div className="orders-mobile-cards">
              {visibleOrders.length > 0 ? (
                visibleOrders.map(renderOrderCard)
              ) : (
                <div className="order-history-empty-list">
                  {isLoadingOrders
                    ? "Loading orders..."
                    : "No orders found for this status"}
                </div>
              )}
            </div>
            <div
              className="orders-table-scroll"
              role="region"
              aria-label="Orders table"
              tabIndex={0}
            >
              <table
                className="orders-table"
                aria-label={`${activeTab?.title || "Orders"} list`}
                aria-busy={isLoadingOrders}
              >
                <thead>
                  <tr>
                    <th scope="col">Order / Status</th>
                    <th scope="col">Customer</th>
                    {/* <th scope="col">Product</th> */}
                    <th scope="col" className="orders-table-price">
                      Item Price
                    </th>
                    <th scope="col" className="orders-table-price">
                      Total
                    </th>
                    <th scope="col">Delivery Address</th>
                    {/* <th scope="col">Actions</th> */}
                  </tr>
                </thead>
                <tbody>
                  {visibleOrders.length > 0 ? (
                    visibleOrders.map(renderOrderRow)
                  ) : (
                    <tr>
                      <td colSpan={5} className="orders-table-empty">
                        {isLoadingOrders
                          ? "Loading orders..."
                          : "No orders found for this status"}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="order-history-pagination">
              <div className="order-history-pagination-summary">
                {pagination.total > 0
                  ? `Showing page ${pagination.page + 1} of ${
                      pagination.totalPages
                    }`
                  : "No results"}
              </div>
              <div className="order-history-pagination-controls">
                <button
                  type="button"
                  className="order-history-filter-action"
                  onClick={() => handlePageChange(-1)}
                  disabled={isLoadingOrders || pagination.page <= 0}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="order-history-filter-action primary"
                  onClick={() => handlePageChange(1)}
                  disabled={
                    isLoadingOrders ||
                    pagination.totalPages === 0 ||
                    pagination.page >= pagination.totalPages - 1
                  }
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Order;
